import { memo, useMemo, type CSSProperties } from 'react'
import { History, SkipForward } from 'lucide-react'
import {
  CLOCK_SEASON_CLASS as SEASON_CLASS, clockChangeGaps, clockChangeMarkerText, clockChangeOf,
  clockChangeSentence, clockChangeTitle, type ClockChange,
} from '../../lib/clock-change.js'
import { addBroadcastDays, broadcastDayKey, broadcastDayLabel, broadcastWeekday } from '../../lib/broadcast-day.js'
import { formatDurationShort } from '../../lib/utils.js'
import './ClockChangeLayer.css'

/**
 * Changement d'heure dans la grille : la règle vit dans `lib/clock-change.ts`,
 * ces composants ne font que la dessiner. Le 25 octobre, la timeline était
 * déjà juste (deux « 02:00 », une journée de 25 h) mais ne le disait pas : on
 * n'y voyait qu'un trou. Bandes, repère de bascule et note du trou se posent
 * sans capter aucun clic : ils commentent la grille sans
 * gêner aucun geste.
 */

interface BandsProps {
  change: ClockChange
  pxPerHour: number
  /** Vue Semaine : repère court, la colonne est étroite. */
  compact?: boolean
  /** Variable CSS de position du conteneur (`--timeline-y` ou `--week-y`). */
  positionVar?: '--timeline-y' | '--week-y'
}

/** Les deux passages de l'heure rejouée, puis le repère de bascule. */
export const ClockChangeBands = memo(function ClockChangeBands({
  change, pxPerHour, compact = false, positionVar = '--timeline-y',
}: BandsProps) {
  const passes = [
    change.firstPass && { range: change.firstPass, season: change.before.season },
    change.secondPass && { range: change.secondPass, season: change.after.season },
  ].filter(Boolean) as { range: { fromHours: number; toHours: number }; season: 'ÉTÉ' | 'HIVER' }[]
  const markerStyle = { [positionVar]: `${Math.round(change.atHours * pxPerHour)}px` } as CSSProperties
  const Icon = change.kind === 'BACKWARD' ? History : SkipForward
  const label = compact
    ? change.kind === 'BACKWARD' ? `${change.after.clock} rejouée` : `${change.before.clock} sautée`
    : clockChangeMarkerText(change)

  return (
    <>
      {passes.map(({ range, season }) => (
        <div
          key={season}
          className={`agenda-clock-band ${SEASON_CLASS[season]}`}
          style={{
            [positionVar]: `${Math.round(range.fromHours * pxPerHour)}px`,
            '--clock-band-height': `${Math.round((range.toHours - range.fromHours) * pxPerHour)}px`,
          } as CSSProperties}
          aria-hidden="true"
        />
      ))}
      <div className={`agenda-clock-marker${compact ? ' is-compact' : ''}`} style={markerStyle}>
        <span className="agenda-clock-marker__label" title={clockChangeSentence(change)}>
          <Icon size={compact ? 10 : 12} aria-hidden="true" />
          {label}
        </span>
      </div>
    </>
  )
})

/**
 * Note posée dans le trou du premier passage. Elle n'existe que tant que le
 * trou existe : une fois la nuit comblée, il ne reste que les bandes.
 */
export const ClockChangeGapNotes = memo(function ClockChangeGapNotes({
  change, date, items, pxPerHour,
}: {
  change: ClockChange | null
  date: string
  items: ReadonlyArray<{ startTime: string; endTime: string; status?: string }>
  pxPerHour: number
}) {
  const gaps = useMemo(() => clockChangeGaps(change, date, items), [change, date, items])
  if (!change || gaps.length === 0) return null
  const slot = `${change.after.clock.slice(0, 2)}:xx`

  return (
    <>
      {gaps.map(gap => {
        const top = Math.round(gap.fromHours * pxPerHour)
        const height = Math.round((gap.toHours - gap.fromHours) * pxPerHour)
        // Trop court pour porter une phrase : les bandes suffisent.
        if (height < 28) return null
        return (
          <div
            key={gap.fromHours}
            className="agenda-clock-gap"
            style={{ '--timeline-y': `${top}px`, '--clock-gap-height': `${height}px` } as CSSProperties}
          >
            <span className="agenda-clock-gap__text">
              Heure supplémentaire à combler · les créneaux de modèle à {slot} se placent au 2e passage.
            </span>
            <span className="agenda-clock-gap__duration">{formatDurationShort(gap.durationSec)}</span>
          </div>
        )
      })}
    </>
  )
})

/**
 * Bandeau d'information au-dessus de la grille, jour ou semaine. Une phrase,
 * deux étiquettes : la durée réelle de la journée et le sens du changement.
 */
export const ClockChangeBanner = memo(function ClockChangeBanner({ date, view }: {
  date: string
  view: 'agenda' | 'week'
}) {
  const found = useMemo(() => {
    if (view === 'agenda') {
      const change = clockChangeOf(date)
      return change ? { day: broadcastDayKey(date), change } : null
    }
    const monday = addBroadcastDays(date, 1 - broadcastWeekday(date))
    for (let index = 0; index < 7; index += 1) {
      const day = broadcastDayKey(addBroadcastDays(monday, index))
      const change = clockChangeOf(day)
      if (change) return { day, change }
    }
    return null
  }, [date, view])

  if (!found) return null
  const { day, change } = found
  const dayLabel = broadcastDayLabel(day, { weekday: 'long', day: 'numeric', month: 'long' })

  return (
    <div className="agenda-clock-banner" role="note">
      <History size={14} aria-hidden="true" className="agenda-clock-banner__icon" />
      {view === 'week' && <strong className="agenda-clock-banner__day">{dayLabel}</strong>}
      <span className="agenda-clock-banner__chip is-length">{change.dayHours} h d’antenne</span>
      <span className={`agenda-clock-banner__chip ${SEASON_CLASS[change.after.season]}`}>{clockChangeTitle(change)}</span>
      <span className="agenda-clock-banner__text">{clockChangeSentence(change)}</span>
    </div>
  )
})
